import { signalStoreFeature, type, withMethods } from '@ngrx/signals';
import { WidgetState } from './widget.state';
import { inject } from '@angular/core';
import { IndicatorStore } from '../indicator/indicator.state';
import { DataLayerStore } from '../data-layer/data-layer.state';
import { IndicatorDataStore } from '../indicatpr-data/indicator-data.state';
import { WidgetHelperService } from '../../services/widget/widget-helper.service';
import { DataGroupProperty } from '../indicatpr-data/indicator-data.widget-selectors';
import { EntityId } from '@ngrx/signals/entities';
import { withWidgetCoreFeatures } from './widget.core-features';
import { makeUniqueStringArray } from '../../utility/array.utility';
import { ChartDataset } from 'chart.js';
import {
  CustomGridStackWidget,
  RegressionData,
  ScatterData,
  WegweiserData,
  WegweiserIndicator,
  WegweiserRegion,
  WidgetConfig,
  WidgetType,
} from '../../services/grid-widget-config.service';
import { ComparisonValueStore } from '../comparison-value/comparison-value.state';
import { ComparisonValue } from '../../models/geo-data';
import { updateAlpha } from '../../utility/color.utility';
import { DataLayer } from '../../models/data-layer';
import { GridStore } from './grid.state';
import { withWegweiserMethods } from './widget.wegweiser-methods';
import { applyDatasetStyling } from '../../utility/chart-styling.utility';
import { getMinValue, getMaxValue, normalize } from '../../utility/chart.utility';
import { ChartGroup, StyledDataset, BaseDataset } from './chart.types';

// ==================== UTILITY FUNCTIONS ====================

/**
 * Sorts chart group data including labels, keys, and datasets. Allows optional sorting
 * functionality based on specified criteria and direction.
 *
 * @param {string[]} labels - An array of chart labels corresponding to data points.
 * @param {string[]} keys - An array of unique keys corresponding to the chart labels.
 * @param {(BaseDataset | StyledDataset)[]} datasets - An array of dataset objects, each containing a label and array of numeric data values.
 * @param {boolean} enableSorting - A boolean value that determines whether sorting should be performed.
 * @param {'label'|'value'} [sortBy] - The criteria to sort by, either 'label' for sorting by label names, or 'value' for sorting by aggregate data values.
 * @param {'asc'|'desc'} [direction] - The sorting direction, either 'asc' for ascending or 'desc' for descending order.
 * @return {{ labels: string[]; datasets: (BaseDataset | StyledDataset)[]; groups: ChartGroup[] }} An object including the sorted labels array, updated datasets, and the computed groups data.
 */
function sortChartGroups(
  labels: string[],
  keys: string[],
  datasets: (BaseDataset | StyledDataset)[],
  enableSorting: boolean,
  sortBy?: 'label' | 'value',
  direction?: 'asc' | 'desc'
): {
  labels: string[];
  datasets: (BaseDataset | StyledDataset)[];
  groups: ChartGroup[];
} {
  // Create an array of objects representing groups
  const groups: ChartGroup[] = labels.map((label, index) => ({
    label: String(label),
    key: String(keys[index]),
    values: datasets.map(dataset => {
      const key = String(keys[index]);
      const dataIndex = dataset.dataKeys?.indexOf(key) || 0;
      return dataset.data[dataIndex] as number;
    }),
  }));

  if (!sortBy || !direction || !enableSorting) {
    return { labels, datasets, groups };
  }

  groups.sort((a, b) => {
    let compareA: string | number;
    let compareB: string | number;

    if (sortBy === 'label') {
      compareA = a.label.toLowerCase();
      compareB = b.label.toLowerCase();
    } else if (sortBy === 'value') {
      compareA = a.values!.reduce((sum, val) => sum + Number(val || 0), 0);
      compareB = b.values!.reduce((sum, val) => sum + Number(val || 0), 0);
    } else {
      throw new Error('Invalid sortBy criteria. Use "label" or "value".');
    }

    if (direction === 'asc') {
      return compareA < compareB ? -1 : compareA > compareB ? 1 : 0;
    } else {
      return compareA > compareB ? -1 : compareA < compareB ? 1 : 0;
    }
  });

  // Extract sorted labels and datasets
  const sortedLabels = groups.map(group => group.label);
  const sortedDatasets = datasets.map((dataset, datasetIndex) => ({
    ...dataset,
    data: groups.map(group => group.values![datasetIndex]),
    dataKeys: groups.map(group => group.key),
  }));

  return { labels: sortedLabels, datasets: sortedDatasets, groups };
}

export function withChartMethods() {
  return signalStoreFeature(
    {
      state: type<WidgetState>(),
    },
    withWidgetCoreFeatures(),
    withWegweiserMethods(),
    withMethods(
      (
        store,
        indicatorStore = inject(IndicatorStore),
        layerStore = inject(DataLayerStore),
        indicatorDataStore = inject(IndicatorDataStore),
        gridStore = inject(GridStore),
        comparisonStore = inject(ComparisonValueStore),
        helper = inject(WidgetHelperService)
      ) => {
        // Destructure from store (includes methods from withWidgetCoreFeatures and withWegweiserMethods)
        const { activeWidget, widgetType, selected, update, entityMap, get, getGroupedWegweiserData } = store as any;
        /**
         * Processes and retrieves an array of labels or numerical values based on the provided group property in the widget configuration.
         *
         * @function getLabelsByGroupProperty
         * @param {WidgetConfig} config - The configuration object for the widget containing properties to determine how labels are grouped and retrieved.
         * @param {string[]} dataLayerIds - An array of data layer IDs to be processed when the group property is 'dataLayer'.
         * @param {string[]} dataKeys - An array of data keys used to retrieve features when the group property is 'feature'.
         * @returns {string[] | number[]} Returns an array of labels (strings) or years (numbers), depending on the group property specified in the configuration.
         *
         * The function behaves differently based on the `groupProperty` field in the `config` object:
         * - 'indicator' - Returns either the full indicator name or its default name, depending on the `showFullIndicatorName` property.
         * - 'feature' - Returns feature names based on provided `dataKeys`.
         * - 'dataLayer' - Returns an array of numeric values (years) derived from the `dataLayerIds`, excluding any disabled years in the `config`.
         * If the `config` object is missing or invalid, the function returns an empty array.
         */
        const getLabelsByGroupProperty = (
          config: WidgetConfig,
          dataLayerIds: string[],
          dataKeys: string[]
        ): string[] | number[] => {
          if (!config) return [];

          switch (config.groupProperty) {
            case 'indicator':
              return indicatorStore
                .getByIds(config.indicators)
                .map(indicator =>
                  config.showFullIndicatorName
                    ? indicatorStore.getFullName(indicator)
                    : indicator.name
                );
            case 'feature':
              // eslint-disable-next-line no-case-declarations
              const features: any = {
                ...config.features,
                ...comparisonStore.byKey(),
              };
              return dataKeys.map(key => features[key].name);
            default: // dataLayer
              return layerStore
                .getByIds(dataLayerIds)
                .map((layer: DataLayer) => Number(layer.year))
                .filter(year => !config.disabledYears.includes(year))
                .sort((a, b) => b - a);
          }
        };

        /**
         * Retrieves an array of keys based on the group property defined in the configuration.
         *
         * This function processes a configuration object and determines the relevant keys
         * based on the specified groupProperty. It supports three types of grouping:
         * 'indicator', 'feature', and the default 'dataLayer'.
         *
         * @param {WidgetConfig} config - The widget configuration object which includes the
         *                                grouping property and additional sub-properties
         *                                necessary for fetching the keys.
         * @param {string[]} dataLayerIds - Array of data layer identifiers used when the
         *                                  groupProperty is 'dataLayer'.
         * @param {string[]} dataKeys - Array of data keys used when the groupProperty is 'feature'.
         *
         * @returns {string[]} An array of keys that correspond to the specified group property.
         */
        const getKeysByGroupProperty = (
          config: WidgetConfig,
          dataLayerIds: string[],
          dataKeys: string[]
        ) => {
          if (!config) return [];
          switch (config.groupProperty) {
            case 'indicator':
              return indicatorStore
                .getByIds(config.indicators)
                .map(indicator => indicator._id);
            case 'feature':
              // eslint-disable-next-line no-case-declarations
              const features: any = {
                ...config.features,
                ...comparisonStore.byKey(),
              };
              return dataKeys.map(key => features[key].key);
            default: // dataLayer
              return layerStore
                .getByIds(dataLayerIds)
                .filter(
                  layer => !config.disabledYears.includes(Number(layer.year))
                )
                .sort((a, b) => b.year - a.year)
                .map(d => d._id);
          }
        };

        /**
         * Generates a dataset label based on the provided widget configuration, property type, key, and other optional parameters.
         *
         * @param {WidgetConfig} widget - The widget configuration object. Determines the context for generating the label.
         * @param {string} property - The type of property to generate the label for. Supported values are 'indicator', 'feature', or other fallback types.
         * @param {string} key - The unique key or identifier used to retrieve the label value from the respective store.
         * @param {boolean} [indicatorPrefix=false] - Optional indicator prefix flag. If true and the property is 'indicator', the label includes its parent indicator name as a prefix.
         * @param {Record<string, ComparisonValue>} comparisonValues - A record of comparison values used to derive the label for 'feature' type properties when applicable.
         * @returns {string} The generated dataset label based on the provided parameters. Returns an empty string if no valid label is determined.
         */
        const getDatasetLabel = (
          widget: WidgetConfig,
          property: string,
          key: string,
          indicatorPrefix = false,
          comparisonValues: Record<string, ComparisonValue>
        ) => {
          if (!widget) return '';

          switch (property) {
            case 'indicator':
              if (indicatorPrefix) {
                let prefix = '';
                const indicator = indicatorStore.getById(key);
                if (!indicator) return '';
                if (indicator.parent) {
                  prefix = indicatorStore.getById(indicator.parent).name;
                }
                return prefix ? `${prefix}: ${indicator.name}` : indicator.name;
              } else {
                return indicatorStore.getById(key)?.name || '';
              }
            case 'feature':
              return (
                widget.features?.[key]?.name ||
                comparisonValues[key]?.name ||
                ''
              );
            default:
              return String(layerStore.getById(key)?.year || '');
          }
        };

        /**
         * Retrieves the group name associated with a specific widget property and key.
         *
         * This function evaluates the provided property and fetches the corresponding group
         * name from the relevant store, based on the key. If the widget object is invalid or the
         * property is unsupported, the function returns `null`.
         *
         * @param {WidgetConfig} widget - The widget configuration object.
         * @param {string} property - The property of the widget to evaluate.
         *                           Supported value: 'indicator'.
         * @param {string} key - The unique key used to fetch the relevant group name.
         * @returns {string | null} - The name of the group associated with the key if available,
         *                           otherwise `null`.
         */
        const getGroup = (
          widget: WidgetConfig,
          property: string,
          key: string
        ) => {
          if (!widget) return null;

          switch (property) {
            case 'indicator':
              return indicatorStore.getParent(key)?.name || null;
            default:
              return null;
          }
        };

        /**
         * Retrieves legend groups based on a widget's configuration.
         *
         * This function processes the `widget` parameter to determine grouping information
         * for indicators, organizing them into a record structure where the keys represent
         * parent group names and the values are arrays of indicators' names belonging to those groups.
         * If the widget does not meet the required conditions or has no valid group properties,
         * the function returns `null`.
         *
         * @param {WidgetConfig} widget - The widget configuration object containing details about indicators and grouping properties.
         * @returns {Record<string, string[]> | null} An object mapping parent group names to arrays of indicator names,
         *                                           or `null` if no grouping information is found.
         */
        const getLegendGroups = (
          widget: WidgetConfig
        ): Record<string, string[]> | null => {
          if (!widget) return null;

          if (widget.groupProperty === 'indicator') {
            const sets: Record<string, string[]> = {};
            widget.indicators.forEach((id: string) => {
              const parentName = indicatorStore.getParent(id)?.name;
              if (!parentName) return;
              if (!sets[parentName]) {
                sets[parentName] = [];
              }
              sets[parentName].push(indicatorStore.getById(id).name);
            });
            return sets;
          }
          return null;
        };

        /**
         * Retrieves the first suitable property from a predefined set of possible properties
         * that is neither the focusProperty nor the groupProperty of the provided configuration.
         *
         * @function
         * @param {WidgetConfig} conf - The configuration object containing properties used to determine
         *                              the appropriate selection.
         * @returns {DataGroupProperty} - The first property from the predefined set ('feature', 'indicator',
         *                                'dataLayer') that is not included in conf.focusProperty or conf.groupProperty.
         */
        const dataSetProperty = (conf: WidgetConfig) => {
          const possibleProps: DataGroupProperty[] = [
            'feature',
            'indicator',
            'dataLayer',
          ];
          return possibleProps.find(
            prop => ![conf.focusProperty, conf.groupProperty].includes(prop)
          )!;
        };

        /**
         * Calculates and generates an array of scatter plot data from the given inputs.
         *
         * This function takes in arrays of x-coordinates, y-coordinates, and optional radii (rs),
         * and returns a collection of scatter data objects. If the radii array is provided,
         * it normalizes the radius values to fit within a specified range (default of 1 to 20).
         *
         * Each scatter data object includes:
         * - The x-coordinate (mandatory)
         * - The y-coordinate (mandatory)
         * - The scaled radius (`r`) normalized to the range if `rs` is provided, or set to a default of 1
         * - The raw, unscaled radius (`rRaw`) if provided in the input, or undefined otherwise
         *
         * Any data where the x or y value is undefined will be filtered out from the resultant array.
         *
         * @param {number[]} xs - The array of x-coordinates for scatter plot data.
         * @param {number[]} ys - The array of y-coordinates for scatter plot data.
         * @param {number[] | null} rs - An optional array of radii corresponding to each (x, y) pair. Can be null.
         * @returns {ScatterData[]} An array of scatter data objects containing x, y, scaled radius (`r`), and raw radius (`rRaw`) values.
         */
        const calculateScatterData = (
          xs: number[],
          ys: number[],
          rs: number[] | null
        ): ScatterData[] => {
          let minR: number | undefined;
          let maxR: number | undefined;

          if (rs) {
            // Calculate min and max of the `r` values
            minR = Math.min(...rs);
            maxR = Math.max(...rs);
          }

          return xs
            .map((x: number, i: number) => {
              const rawR = rs && rs[i] ? rs[i] : 1;
              const scaledR =
                rs && maxR !== undefined && minR !== undefined
                  ? normalize(rawR, minR, maxR, 1, 20)
                  : 1;

              return {
                x,
                y: ys[i],
                r: scaledR,
                rRaw: rs?.[i] || undefined,
              };
            })
            .filter(
              (data: ScatterData) =>
                data.x !== undefined && data.y !== undefined
            );
        };

        /**
         * Converts grouped data into axis data for a chart, such as scatter or bubble chart data.
         *
         * @param {Record<string, Record<string, number | null>>} groupedData - The grouped data input, structured in a nested key-value format, where the outer keys represent data fields, and the inner keys represent individual data points.
         * @param {WidgetConfig} config - Configuration object for the widget, containing axis properties, chart settings, colorset, and other display customization options.
         * @param {WidgetType} type - The type of widget or chart being generated. Can include types such as 'adder', 'scatter', or 'bubble'.
         * @param {Record<string, ComparisonValue>} comparisonValues - Mapping of comparison values used for computations or supporting additional data labels.
         *
         * @returns {ChartDataset[] | undefined} Returns an array of chart datasets formatted for chart rendering. If regression is enabled in the configuration, regression data is included. Returns `undefined` when inputs are invalid or insufficient for processing.
         */
        const convertToAxisData = (
          groupedData: Record<string, Record<string, number | null>>,
          config: WidgetConfig,
          type: WidgetType,
          comparisonValues: Record<string, ComparisonValue>
        ) => {
          type = type || 'adder';
          const ids = config?.axisProperties.length
            ? config.axisProperties
            : [...Object.keys(groupedData).slice(0, 2)];

          if (type === 'bubble') {
            const radiusValue = Object.keys(groupedData)[2];
            if (radiusValue) ids.push(radiusValue);
          }

          if (!config || !groupedData || !type) {
            return;
          }

          if (
            !ids ||
            (ids.length < 2 && type === 'scatter') ||
            (ids.length < 3 && type === 'bubble')
          ) {
            return;
          }

          const radiusProperty =
            config?.radiusProperty || Object.keys(groupedData)[2] || null;
          if (radiusProperty) {
            ids.push(radiusProperty);
          }

          let label = 'x: ';
          const chartData: ChartDataset = {
            data: [],
            backgroundColor: helper.getChartBackgroundColor(
              type,
              config.colorset,
              config.invertColors,
              0
            ),
            borderColor: helper.getChartBackgroundColor(
              type,
              config.colorset,
              config.invertColors,
              0
            ),
            label: '',
            pointRadius: config.dotSize || 4,
          };

          const dataItems: any = ids.map((id, idx) => {
            const dataLabel = getDatasetLabel(
              config,
              dataSetProperty(config),
              id,
              true,
              comparisonValues
            );
            if (idx === 1) {
              label += ' | y: ';
              config.xAxisLabel = config.axis?.x.label || dataLabel;
            } else if (idx === 0) {
              config.yAxisLabel = config.axis?.y.label || dataLabel;
            }

            label += dataLabel;

            return groupedData[id];
          });

          if (
            !dataItems ||
            !dataItems[0] ||
            !dataItems[1] ||
            dataItems.length < 2
          ) {
            return;
          }

          const xs: number[] = Object.values(dataItems[0]);
          const ys: number[] = Object.values(dataItems[1]);
          const rs: number[] | null = dataItems[2]
            ? Object.values(dataItems[2])
            : null;

          const converted: ScatterData[] = calculateScatterData(
            xs,
            ys,
            rs
          ).filter(data => data.x !== undefined && data.y !== undefined);

          chartData.data = converted;
          chartData.label = label;

          if (config.displayRegression) {
            const regressionData: RegressionData =
              helper.calcRegression(converted);
            const regressionDataset = {
              regression: true,
              label: 'r2: ' + regressionData.r2,
              customTooltip: 'r2: ' + regressionData.r2,
              r2: regressionData.r2,
              data: regressionData.line,
              type: 'line',
              borderColor: 'rgba(255, 99, 132, 1)',
              borderWidth: 2,
              fill: false,
            };

            return [chartData, regressionDataset];
          } else {
            return [chartData];
          }
        };


        /**
         * Gets valid keys by filtering out disabled items based on the dataset property.
         * The dsProperty determines what the datasets represent (feature, indicator, or dataLayer).
         *
         * @param config - The widget configuration
         * @param data - The data object to extract keys from
         * @param dsProperty - The property that determines what the datasets represent
         * @param dataLayerIds - Array of data layer IDs (used when dsProperty is 'dataLayer')
         * @returns Filtered array of valid keys
         */
        function getValidKeys(
          config: WidgetConfig,
          data: Record<string, any>,
          dsProperty: DataGroupProperty,
          dataLayerIds: string[]
        ): string[] {
          const keys = Object.keys(data);

          if (!config) return keys;

          switch (dsProperty) {
            case 'feature':
              // Filter out disabled features
              // eslint-disable-next-line no-case-declarations
              const disabledFeatures = config.disabledFeatures || [];
              // eslint-disable-next-line no-case-declarations
              const availableFeatureKeys = config.features
                ? Object.keys(config.features)
                : [];

              return keys.filter(
                key =>
                  !disabledFeatures.includes(key) &&
                  (availableFeatureKeys.length === 0 ||
                    availableFeatureKeys.includes(key))
              );

            case 'dataLayer':
              // Filter out disabled years by checking the dataLayerIds
              // eslint-disable-next-line no-case-declarations
              const disabledYears = config.disabledYears || [];

              return dataLayerIds.filter(layerId => {
                const layer = layerStore.getById(layerId);
                return layer && !disabledYears.includes(Number(layer.year));
              });

            case 'indicator':
              // Indicators don't need filtering - return all keys
              return keys;

            default:
              return keys;
          }
        }

        /**
         * Creates a base dataset without styling (styling will be applied later after sorting).
         *
         * @param {Record<string, number | null>} data - The input data for the dataset.
         * @param {WidgetConfig} config - Configuration settings for the dataset.
         * @param {WidgetType} type - The widget type (e.g., chart type).
         * @param {string} labelKey - The key used to identify the label for the dataset.
         * @param {number} idx - The current index, used for color variations.
         * @param {string[]} dataKeys - An array of keys representing data fields used in the dataset.
         * @param {Record<string, ComparisonValue>} keyValueComparisonValues - A mapping of keys to their comparison values.
         * @param {DataGroupProperty} dsProperty - A property key that determines specific dataset behavior or grouping.
         *
         * @returns {StyledDataset} The formatted dataset object without styling applied.
         */
        const createDataset = (
          data: Record<string, number | null>,
          config: WidgetConfig,
          type: WidgetType,
          labelKey: string,
          idx: number,
          dataKeys: string[],
          keyValueComparisonValues: Record<string, ComparisonValue>,
          dsProperty: DataGroupProperty
        ): StyledDataset => {
          const label = getDatasetLabel(
            config,
            dsProperty,
            labelKey,
            false,
            keyValueComparisonValues
          );
          const tooltip = getDatasetLabel(
            config,
            dsProperty,
            labelKey,
            true,
            keyValueComparisonValues
          );

          const color = helper.getChartBackgroundColor(
            type,
            config.colorset,
            config.invertColors,
            idx,
            config.featureOpacity || 1,
            Object.values(data).length
          );

          return {
            data: Object.values(data),
            dataKeys: dataKeys,
            backgroundColor: color,
            borderColor: updateAlpha(
              color,
              config.featureOpacity ? config.featureOpacity + 0.4 : 1
            ),
            label: label,
            customTooltip: tooltip,
            labelKey: labelKey,
            featureType: dataSetProperty(config),
            pointRadius: config.dotSize || 4,
            borderWidth: config.borderWidth || 2,
            tension: config.lineTension || 0,
            group: getGroup(config, dsProperty, labelKey),
          };
        };

        /**
         * Applies styling to datasets based on selected and highlighted features.
         * This should be called AFTER sorting to ensure styling is applied to correct indices.
         *
         * @param {any[]} datasets - The array of datasets to apply styling to.
         * @param {WidgetConfig} config - Configuration settings for the widget.
         * @param {WidgetType} type - The widget type (e.g., chart type).
         * @param {string[]} sortedKeys - The sorted keys (from groups after sorting).
         * @param {Array<{ key: string; type: string }> | null} selectedFeatures - Selected features to highlight.
         * @param {{ key: string; type: string } | undefined} highlightedFeature - The highlighted feature.
         * @param {string} dsProperty - The dataset property type.
         *
         * @returns {any[]} The datasets with styling applied.
         */

        const addComparisonDatasets = (
          datasets: any[],
          config: WidgetConfig,
          type: WidgetType,
          comparisonData: any,
          featureData: any
        ) => {
          if (
            type !== 'bar' ||
            !config.comparisonValues ||
            !config.comparisonValues.length
          ) {
            return datasets;
          }

          datasets = datasets.map((dataset: any) => {
            dataset.type = 'bar';
            return dataset;
          });

          const compSets: any[] = [];
          config.comparisonValues.forEach((compKey, compIndex) => {
            const data = comparisonData[compKey];
            if (
              data &&
              (config.groupProperty === 'feature' ||
                config.focusProperty === 'feature')
            ) {
              Object.values(data).forEach((item: any, idx) => {
                const labelKey = Object.keys(featureData)[idx];
                const color = helper.getChartBackgroundColor(
                  type,
                  config.colorset,
                  config.invertColors,
                  idx
                );
                compSets.push(
                  helper.getComparisonLineOptions(
                    Object.values(item),
                    Object.keys(item),
                    {
                      color,
                      compIndex,
                      legendIndex: idx,
                      labelKey: labelKey,
                      tooltip: comparisonStore.getNameByKey(compKey),
                      asBar: !datasets.length,
                    }
                  )
                );
              });
            } else if (data) {
              compSets.push(
                helper.getComparisonLineOptions(
                  Object.values(data),
                  Object.keys(data),
                  {
                    color: 'red',
                    tooltip: comparisonStore.getNameByKey(compKey),
                    asBar: !datasets.length,
                  }
                )
              );
            }
          });

          datasets = datasets.concat(compSets);
          return datasets;
        };

        // ==================== MAIN UPDATE METHOD ====================

        const createLocalDatasets = (
          type: WidgetType,
          config: WidgetConfig,
          featureData: Record<string, any>,
          comparisonData: any,
          dsProperty: DataGroupProperty,
          keyValueComparisonValues: Record<string, ComparisonValue>
        ) => {
          let datasets: (BaseDataset | StyledDataset)[] = [];
          let layerIds: string[] = [];
          let dataKeys: string[] = [];

          if (helper.isAxisBasedType(type || 'adder')) {
            datasets = (convertToAxisData(
              featureData,
              config,
              type,
              keyValueComparisonValues
            ) as (BaseDataset | StyledDataset)[]) || [];
          } else {
            datasets = Object.values(featureData).map((item, idx) => {
              const labelKey = Object.keys(featureData)[idx];
              const setKeys = Object.keys(item);
              dataKeys = setKeys;

              layerIds = layerIds.concat(
                config.groupProperty === 'dataLayer'
                  ? Object.keys(item)
                  : Object.keys(featureData)
              );

              return createDataset(
                item,
                config,
                type,
                labelKey,
                idx,
                dataKeys,
                keyValueComparisonValues,
                dsProperty
              );
            });

            datasets = addComparisonDatasets(
              datasets,
              config,
              type,
              comparisonData,
              featureData
            );
          }

          return { datasets, layerIds, dataKeys };
        };

        const finalizeChartData = (
          config: WidgetConfig,
          type: WidgetType,
          datasets: (BaseDataset | StyledDataset)[],
          dataLayerIds: string[],
          dataKeys: string[],
          layerIds: string[],
          groupedData: any,
          dsProperty: DataGroupProperty,
          selectedFeatures: Array<{ key: string; type: string }> | null,
          highlightedFeature: { key: string; type: string } | undefined,
          widgetById: CustomGridStackWidget | null,
          rerender: boolean
        ) => {
          const labels = getLabelsByGroupProperty(
            config,
            dataLayerIds,
            dataKeys
          );
          const keys = getKeysByGroupProperty(config, dataLayerIds, dataKeys);

          // Sort the chart groups first
          config.chartData = sortChartGroups(
            labels as string[],
            keys as string[],
            datasets,
            config.sort.enable,
            config.sort.property,
            config.sort.direction
          );

          // Now apply styling based on the sorted keys
          const sortedKeys = config.chartData.groups.map((group: ChartGroup) => group.key);
          config.chartData.datasets = applyDatasetStyling(
            config.chartData.datasets,
            config,
            type,
            sortedKeys,
            selectedFeatures,
            highlightedFeature,
            dsProperty,
            helper
          );

          config.chartData.legendGroups = getLegendGroups(config);
          config.chartData.minValue = getMinValue(datasets);
          config.chartData.maxValue = getMaxValue(datasets);
          config.groupedData = groupedData;
          config.dataLayers = makeUniqueStringArray(layerIds);
          config.dataSetProperty = dsProperty;

          const current = widgetById || { ...selected() };
          current.widget = config;
          update(current.id as string, current, rerender);
        };

        const updateChartData = (id?: EntityId, rerender = true) => {
          const selectedFeatures =
            gridStore.options()?.selectedFeatures || null;
          const highlightedFeature = gridStore.options()?.highlightedFeature;
          let widgetById: CustomGridStackWidget | null = null;
          const keyValueComparisonValues = comparisonStore.byKey();

          if (id) {
            widgetById = get(id);
          }

          const config = widgetById ? widgetById.widget : activeWidget();
          const type = widgetById ? widgetById.type : widgetType();

          if (!config || !type) return null;

          // force sort overwrite when group property is dataLayer (years)
          if (config.groupProperty === 'dataLayer') {
            config.sort = {
              enable: true,
              property: 'label',
              direction: 'asc',
            };
          }
          // Handle external/wegweiser data
          if (config.context !== 'local') {
            const data = getGroupedWegweiserData(id as string);

            if (data && data.datasets) {
              config.chartData = {
                datasets: data.datasets,
                labels: data.labels,
                minValue: getMinValue(data.datasets),
                maxValue: getMaxValue(data.datasets),
              };

              const current = widgetById || { ...selected() };
              current.widget = config;
              update(current.id as string, current, rerender);
            }
            return null;
          }

          // Handle local data
          const dsProperty = dataSetProperty(config);
          const groupedData = indicatorDataStore.groupByProperties(
            config[config.focusProperty],
            config.focusProperty,
            config.groupProperty,
            dsProperty,
            {
              indicators: config.indicators,
              disabledYears: config.disabledYears,
              dataLayerType: config.dataLayerType,
              disabledDataLayers: layerStore.getIdsByYears(
                config.dataLayerType,
                config.disabledYears
              ),
              disabledFeatures: config.disabledFeatures,
              availableFeatureKeys: Object.keys(config.features || []),
              comparisonKeys: Object.keys(keyValueComparisonValues),
            }
          );

          const { datasets, layerIds, dataKeys } = createLocalDatasets(
            type,
            config,
            groupedData.features,
            groupedData.comparison,
            dsProperty,
            keyValueComparisonValues
          );

          if (datasets) {
            finalizeChartData(
              config,
              type,
              datasets,
              groupedData.dataLayerIds,
              dataKeys,
              layerIds,
              groupedData,
              dsProperty,
              selectedFeatures,
              highlightedFeature,
              widgetById,
              rerender
            );
          }

          return null;
        };

        const getChartData = (id: EntityId) => {
          const widget = entityMap()[id];
          if (!widget || !widget.widget) return null;
          return widget.widget.chartData;
        };

        // ==================== RETURN METHODS ====================

        return {
          getLabelsByGroupProperty,
          getKeysByGroupProperty,
          getDatasetLabel,
          getGroup,
          getLegendGroups,
          dataSetProperty,
          updateChartData,
          calculateScatterData,
          convertToAxisData,
          getChartData,
        };
      }
    )
  );
}
